import { isHttpError, useAuth } from "@mb-pro-ui/utils";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocale } from "../locales/LocaleProvider";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// Rendered only while no session is stored: a login request sent with a
// stored session would carry its bearer token instead of the credentials.
export default function Login() {
  const intl = useIntl();
  const { locale, locales, setLocale } = useLocale();
  const { signin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [install, setInstall] = useState<(() => void) | null>(null);

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstall(() => () => {
        void e.prompt();
        void e.userChoice.then(() => setInstall(null));
      });
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const error = signin.error;
  const errorMessage = !error
    ? " "
    : isHttpError(error) && error.status === 401
      ? intl.formatMessage({ defaultMessage: "Incorrect username or password" })
      : intl.formatMessage(
          { defaultMessage: "Login failed: {message}" },
          { message: error.message },
        );

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        bgcolor: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper elevation={8} sx={{ width: "100%", maxWidth: 380, p: 2.5, borderRadius: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mb: 1.5 }}>
          {locales.map((l) => (
            <ButtonBase key={l} onClick={() => setLocale(l)} disabled={l === locale} sx={{ px: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: l === locale ? 700 : 400 }}>
                {l}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <img src="/favicon.svg" alt="" width={56} height={56} />
        </Box>
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>
          Monitoringbook
        </Typography>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const username = (form.elements.namedItem("username") as HTMLInputElement).value;
            const password = (form.elements.namedItem("password") as HTMLInputElement).value;
            signin.mutate({ username, password });
          }}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            name="username"
            label={intl.formatMessage({ defaultMessage: "Username" })}
            required
            autoComplete="username"
            autoFocus
            disabled={signin.isLoading}
            error={!!error}
            fullWidth
            slotProps={{ htmlInput: { autoCapitalize: "off", autoCorrect: "off", spellCheck: false } }}
          />
          <TextField
            name="password"
            type={showPassword ? "text" : "password"}
            label={intl.formatMessage({ defaultMessage: "Password" })}
            required
            autoComplete="current-password"
            disabled={signin.isLoading}
            error={!!error}
            helperText={errorMessage}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={intl.formatMessage({ defaultMessage: "Show password" })}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button type="submit" variant="contained" size="large" disabled={signin.isLoading}>
            <FormattedMessage defaultMessage="Sign in" />
          </Button>
          {install && (
            <Button variant="text" onClick={install}>
              <FormattedMessage defaultMessage="Install on this device" />
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
